import React, {useEffect, useState} from "react";
import {formatProductTerm, formatCurrency} from "../../utils/helpers";
import Select from "../Form/Select/Select";
import {ToolTipHover} from "../../utils/ToolTip2";
import RadioButton from "../SharedComponents/RadioButton";
import {exceedsMaxBalance} from "./MaturityFlowHelperFunctions";
import {useLogger} from "../../contexts/Logger";

/** @import {ValueType} from "react-select" */
/** @import {OptionType} from "../Form/Select/SelectTypes" */
/** @import {Instruction, MaturityOption} from './MaturityOptionsTypes' */
/** @import {MaturityOptionsProps} from './MaturityOptionsTypes' */
/** @import {Account, Product} from "../../contexts/CustomerDataProviderTypes" */

/** @import {OtherAccount} from '././PartialRenewal/PartialRenewalForm/TransferSection/TransferSectionTypes' */

import './maturityInstructionForm.scss';
import {PartialRenewalForm} from "./PartialRenewal/PartialRenewalForm/PartialRenewalForm";
import currencyInput from "../Form/TextInput/CurrencyInput";
import {MaturityTerm} from "./MaturityTerm";
import CurrencyInput from "../Form/TextInput/CurrencyInput";
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";

function Options(/** @type {{ options: MaturityOption[]; selected: String; onSelect: (selected: string) => void; }} */ {options, selected, onSelect}) {
    return (
        <>
            {options.map(o => {
        const selectedClassName = o.key === selected ? " maturity4__option--selected" : " ";
        return (
            <div key={o.key} className={'maturity3__flex-row--equal-width-child'}>
                {!o.warningText &&
                    <div className={"maturity4__option " + selectedClassName /*+ (o.disabled && ` disabled`)*/}>
                        <RadioButton
                            disabled={o.disabled}
                            classNameOverride={"maturity4"}
                            value={o.key}
                            chosenOption={selected}
                            handleChange={(e) => onSelect(e.target.value)}
                            content={
                                <div className={"content"}>
                                    <h1>{o.title}</h1>
                                    <b data-di-mask>{o.amount}</b>
                                    <p>{o.subtitle}</p>
                                </div>
                            }
                        />
                    </div>
                }
                {o.warningText &&
                    <ToolTipHover tooltipText={o.warningText}>
                        <div className={"maturity4__option " + selectedClassName /*+ (o.disabled && ` disabled`)*/}>
                            <RadioButton
                                hideIconWhenDisabled={false}
                                disabled={o.disabled}
                                classNameOverride={"maturity4"}
                                value={o.key}
                                chosenOption={selected}
                                handleChange={(e) => onSelect(e.target.value)}
                                content={
                                    <div className={"content"}>
                                        <h1>{o.title}</h1>
                                        <b data-di-mask>{o.amount}</b>
                                        <p>{o.subtitle}</p>
                                    </div>
                                }
                            />
                        </div>
                    </ToolTipHover>
                }
            </div>
        );
    })}
        </>
    );
}

export function MaturityOptions(/** @type {MaturityOptionsProps} */ {
    maturingAccount,
    products,
    setInstruction,
    onStepComplete,
    featureEnabled,
    accountList,
    currencyInputValue,
    setCurrencyInputValue,
    selectedAccount,
    setSelectedAccount
}) {

    const analytics = useUnifiedAnalytics();
    const displayMaxBalanceWarning = maturingAccount ? exceedsMaxBalance(maturingAccount) : false;
    const logger = useLogger();

    const onSelectRolloverPayaway = () => {
        analytics.trackAnalyticsClickEvent(
            "select this option_renew original",
            window.location.pathname,
            "component middle",
            maturingAccount?.product.code || '',
            "maturity",
            {
                "maturityOption": "renew original",
                "termOption": products?.find(p => p.id === selectedProduct)?.code
            },
            "link click",
            "selection|click|renew original",
            "maturity"
        );

        onSelectRollover({
            name: "RolloverPayawayInterest",
            options : {
                rolloverAccountProductId: selectedProduct || ''
            }
        });
    }

    const onSelectRolloverAllFunds = () => {
        analytics.trackAnalyticsClickEvent(
            "select this option_renew everything",
            window.location.pathname,
            "component left",
            maturingAccount?.product.code || '',
            "maturity",
            {
                "maturityOption": "renew everything",
                "termOption": products?.find(p => p.id === selectedProduct)?.code
            },
            "link click",
            "selection|click|renew everything",
            "maturity"
        );

        onSelectRollover({
            name: "RolloverAllFunds",
            options : {
                rolloverAccountProductId: selectedProduct || ''
            }
        });
    }

    const onSelectRollover = (/** @type {Instruction} */ instruction) => {

        logger.info(`User selected ${instruction?.name} instruction:`, {
            additionalData: {
                accountId : maturingAccount?.id,
                instruction: instruction,
            }
        });

        if (maturingAccount ? exceedsMaxBalance(maturingAccount) : false) {
            onStepComplete({
                name: "RolloverPayawayInterest",
                options : {
                    rolloverAccountProductId: instruction.options?.rolloverAccountProductId || ''
                }
            });
        } else {
            onStepComplete(instruction);
        }
        window.scrollTo(0, 0);
    }

    const onSelectPartialRollover = (/** @type {Instruction} */ instruction) => {
        analytics.trackAnalyticsClickTransactionEvent(
            instruction?.onComplete?.name || '',
            maturingAccount?.product?.code || '',
            'renew partial',
            maturingAccount?.product?.code || '',
            'maturity-instruction|click|continue',
            'maturity',
            products?.find(p => p.id === selectedProduct)?.code
        );

        // analytics.trackAnalyticsClickEvent(
        //     "select this option_renew partial",
        //     window.location.pathname,
        //     "component middle",
        //     maturingAccount?.product.code || '',
        //     "maturity",
        //     {
        //         "maturityOption": "renew partial",
        //         "partialRolloverAmount": Number(currencyInputValue),
        //         "termOption": products?.find(p => p.id === selectedProduct)?.code,
        //         "balanceAmount": (Number(maturingAccount?.balance) - Number(currencyInputValue)),
        //         "transferAccount": selectedAccount ? selectedAccount.accountNumber : '',
        //         "transferAccountType": selectedAccount ? selectedAccount.product.name : ''
        //     },
        //     "link click",
        //     "selection|click|renew partial",
        //     "maturity"
        // );
        logger.info(`User selected ${instruction?.name} instruction:`, {
            additionalData: {
                accountId: maturingAccount?.id,
                instruction: instruction,
            }
        });

        const balance = Number(maturingAccount?.balance);
        const inputAmount = Number(currencyInputValue);
        const exceedsBalance = maturingAccount ? exceedsMaxBalance(maturingAccount) : false;

        const stepData = {
            name: "RolloverPartialFundPayoutInterest",
            options: {
                rolloverAccountProductId:  instruction?.options?.rolloverAccountProductId ?? selectedProduct,
                partialRolloverAmount: inputAmount.toFixed(2),
                remainingBalanceWithdrawalAmount: (balance - inputAmount).toFixed(2),
                selectedTransferAccount: selectedAccount
            }
        };

        if (balance === inputAmount) {
            stepData.name = "RolloverPayawayInterest";
            onStepComplete(stepData);
        } else {
            onStepComplete(stepData);
        }

        window.scrollTo(0, 0);
    }

    const onSelectPayaway = () => {
        analytics.trackAnalyticsClickEvent(
            "select this option_transfer everything",
            window.location.pathname,
            "component right",
            maturingAccount?.product.code || '',
            "maturity",
            {
                "maturityOption": "transfer everything"
            },
            "link click",
            "selection|click|transfer everything",
            "maturity"
        );

        onStepComplete();
        window.scrollTo(0, 0);
    }

    const getCurrentBalanceWithCalculatedInterest = (/** @type {Account} */ maturingAccount) => {
        if (!maturingAccount.maturity?.calculatedTotalInterest) {
            return maturingAccount.maturity?.calculatedBalanceAtMaturity;
        }

        const currentBalance = maturingAccount.balance;
        const calculatedMaturity = maturingAccount?.maturity?.calculatedTotalInterest;

        return currentBalance + calculatedMaturity;
    }

    const getCalculatedInterest = (/** @type {Account} */ maturingAccount) => {
        if (!maturingAccount.maturity?.calculatedTotalInterest && maturingAccount.maturity?.calculatedBalanceAtMaturity) {
            return maturingAccount.maturity.calculatedBalanceAtMaturity - maturingAccount.balance;
        }

        return maturingAccount.maturity?.calculatedTotalInterest;
    }

    /** @type {MaturityOption[]} */
    const options = [
        {
            key: 'rollover_all_funds',
            instructions: ["RolloverAllFunds"],
            title: 'Renew everything',
            amount: formatCurrency((maturingAccount ? getCurrentBalanceWithCalculatedInterest(maturingAccount) : 0) || 0),
            subtitle: 'Re-invest your entire balance and all the interest you’ve earned.',
            disabled: displayMaxBalanceWarning,
            warningText: displayMaxBalanceWarning ? `This is over the ${maturingAccount?.product.maxWithdrawalLimit ? formatCurrency(maturingAccount.product.maxWithdrawalLimit) : ''} maximum balance for the Fixed Rate Saver` : '',
            showProductOptions: true,
            showProductPartialOptions:false,
            onComplete: () => onSelectRolloverAllFunds()
        },
        {
            key: 'rollover_partial_payaway_interest',
            instructions: ["RolloverPartialFundPayoutInterest"],
            title: 'Renew some or all of your balance',
            amount: formatCurrency(maturingAccount?.balance),
            subtitle: `Re-invest some or all of your balance, and have the rest paid out.`,
            showProductOptions: false,
            disabled: false,
            warningText:'',
            showProductPartialOptions: true,
            onComplete: () => onSelectPartialRollover()
        },
        {
            key: 'payaway_all_funds',
            instructions: ["PayawayLinked", "PayawayOtherAccount"],
            title: 'Transfer everything',
            amount: maturingAccount ? formatCurrency(getCurrentBalanceWithCalculatedInterest(maturingAccount) || 0) : 0,
            subtitle: `Have your entire balance paid into your linked account, or an Online Flexi Saver or 90-Day Notice Saver if you have one.`,
            disabled: false,
            warningText: '',
            showProductOptions: false,
            showProductPartialOptions:false,
            onComplete: () => onSelectPayaway()

        }
    ]

    const findExistingOption = (/** @type {string} */ currentInstruction, /** @type {MaturityOption[]} */ allInstructions) => {
        const existingOption = allInstructions.find(o => o.instructions.includes(currentInstruction));
        return (existingOption && existingOption.key) || null;
    }

    const showProductionOptions = (/** @type {string} */ selectedOption, /** @type {MaturityOption[]} */ allOptions) => {
        const option = allOptions.find(o => o.key === selectedOption);
        return (option && option.showProductOptions) || false;
    }

    const showPartialProductionOptions = (/** @type {string} */ selectedOption, /** @type {MaturityOption[]} */ allOptions) => {
        const option = allOptions.find(o => o.key === selectedOption);
        return (option && option.showProductPartialOptions) || false;
    }
    const findOptionOnComplete = (/** @type {string} */ selectedOption, /** @type {MaturityOption[]} */ allOptions) => {
        const option = allOptions.find(o => o.key === selectedOption);
        return (option && option.onComplete) || null;
    }

    const currentInstruction = findExistingOption(maturingAccount?.maturity?.maturityInstruction || "", options);
    const currentProduct = maturingAccount?.maturity?.rolloverAccountProductId;
    const [option, setOption] = useState(currentInstruction);
    const [selectedProduct, setSelectedProduct] = useState(currentProduct);


    useEffect(() => {
        if (maturingAccount?.maturity?.maturityInstruction === "RolloverPayawayInterest") {
            setOption('rollover_partial_payaway_interest');
            setSelectedAccount(undefined);
        }
    }, [featureEnabled, maturingAccount, setSelectedAccount]);
    
    useEffect( () => {
        if ((!showProductionOptions(option || "", options) &&
                !showPartialProductionOptions(option || "", options))
            || (showProductionOptions(option || "", options) && selectedProduct))
        {
            const instruction = {
                key: option,
                onComplete: option ? findOptionOnComplete(option, options) : null
            };
            setInstruction(instruction);
        }
        else if((showPartialProductionOptions(option || "", options) && selectedProduct)){

            const instruction = {
                key: option,
                onComplete: option ? findOptionOnComplete(option, options) : null,
                ...(selectedAccount && { selectedTransferAccount: selectedAccount })
            };

            setInstruction(instruction);
        }
        else {
            setInstruction(null);
        }
    },[option, selectedProduct, currencyInputValue, selectedAccount])
    
    const handleSelect = (/** @type {string} */ option) => {
        setSelectedProduct(undefined);
        setSelectedAccount(undefined);
        setOption(option);

        const value = maturingAccount?.maturity?.partialRolloverAmount?.toFixed(2)?.toString() ||
            maturingAccount?.balance?.toFixed(2)?.toString() ||
            '0';
        
        const numericCurrencyInputValue = Number(currencyInputValue) || 0;
        const maturingAccountBalance = maturingAccount?.balance || 0;

        if (numericCurrencyInputValue <= 0 || numericCurrencyInputValue > maturingAccountBalance || numericCurrencyInputValue > 250000) {
            setCurrencyInputValue(value);
        }
    }
    
    return (
        <>
            <div className="maturity3__flex-row maturity3__flex-row--responsive" style={{ margin: "0px 0 24px 0" }}>
                <Options
                    options={options}
                    selected={option || ""}
                    onSelect={(o) => handleSelect(o)}
                />
            </div>
            { showProductionOptions(option || "", options) &&
                <>
                <span className={"maturity3__span_content"}>Select your preferred term option</span>
                <div className={"maturity3__flex-row maturity3__flex-row--responsive"}>
                        <MaturityTerm
                            maturingAccount={maturingAccount}
                            products={products}
                            selectedProduct={selectedProduct}
                            setSelectedProduct={setSelectedProduct}
                        />
                    </div>
                </>
            }

            { showPartialProductionOptions(option || "", options) &&
                <>
                <span className={"maturity3__span_content"}>Customise your choice</span>
                    <PartialRenewalForm
                        maturingAccount={maturingAccount}
                        products={products}
                        accountList={accountList}
                        currencyInputValue={currencyInputValue}
                        setCurrencyInputValue={setCurrencyInputValue}
                        selectedAccount={selectedAccount}
                        setSelectedAccount={setSelectedAccount}
                        selectedProduct={selectedProduct}
                        setSelectedProduct={setSelectedProduct}
                    />
                </>
            }
        </>
    );
}